/**
 * 航班验证单元格组件
 *
 * 用于在对账表格的"关联航班"列中展示多维度状态：
 *
 * 状态维度体系：
 * 1. **航班匹配状态** - MATCHED / UNMATCHED / MATCH_FAILED
 *    数据来源：matched_flight_ids.length + additional_info
 *
 * 2. **行项目优先校验状态** - PASSED / HAS_ERRORS / NOT_VALIDATED
 *    数据来源：validationData.flight_results[flight_id].products_matrix；无法唯一归属当前行时降级到航班级状态
 *
 * 3. **航班运营状态** - COMPLETED / PENDING
 *    数据来源：matched_flights[].status
 *
 * 4. **用户修改状态** - MODIFIED / ORIGINAL
 *    数据来源：isModified prop (由调用方通过 dirtyFields 判断)
 *
 * 展示策略：
 * - 单元格内（紧凑视图）：航班标签 + 状态指示器组合（两行高度）
 * - 浮窗内（详情视图）：航班信息、推理过程、校验结果详情
 * - 颜色优先级：红色(错误) > 橙色(警告) > 绿色(正常) > 灰色(待处理)
 */
import React, { useMemo } from "react";
import { Tag, Popover, Space, Divider, Typography, Badge, Button, Tooltip, Descriptions, Empty, Alert } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined, QuestionCircleOutlined, RocketOutlined, InfoCircleOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import FlightTimeDisplay from "../../../../../components/DateTimeDisplay/FlightTimeDisplay";
import { resolveTime } from "../../../../../utils/resolveTime";
import AgentComparisonMatrix, { extractProductsMatrix } from "../../../components/AgentComparisonMatrix";
import { resolveLineItemValidationState, resolveProductsMatrixScope } from "../../utils/reviewPayloadSemantics";
import AllocationInfoBlock from "./AllocationInfoBlock";
import { buildAllocationBreakdown } from "../../utils/allocationCalculator";

dayjs.extend(utc);

const { Text, Title } = Typography;

// 校验状态配置
const VALIDATION_STATUS = {
    PASSED: {
        color: "#52c41a",
        bgColor: "#f6ffed",
        borderColor: "#b7eb8f",
        icon: <CheckCircleOutlined />,
        text: "通过",
        tagColor: "success",
    },
    HAS_ERRORS: {
        color: "#ff4d4f",
        bgColor: "#fff2f0",
        borderColor: "#ffccc7",
        icon: <CloseCircleOutlined />,
        text: "异常",
        tagColor: "error",
    },
    PENDING: {
        color: "#faad14",
        bgColor: "#fffbe6",
        borderColor: "#ffe58f",
        icon: <ClockCircleOutlined />,
        text: "验证中",
        tagColor: "warning",
    },
    NOT_VALIDATED: {
        color: "#8c8c8c",
        bgColor: "#fafafa",
        borderColor: "#d9d9d9",
        icon: <QuestionCircleOutlined />,
        text: "待验证",
        tagColor: "default",
    },
    STALE: {
        color: "#1890ff",
        bgColor: "#e6f4ff",
        borderColor: "#91caff",
        icon: <EditOutlined />,
        text: "待重验",
        tagColor: "processing",
    },
};

/**
 * 获取航班的校验结果
 * @param {number} flightId - 航班ID
 * @param {Object} flightResults - 校验结果映射 {flight_id: result_data}
 * @returns {Object|null} 校验结果对象
 */
const getFlightValidationResult = (flightId, flightResults) => {
    if (!flightId || !flightResults) return null;
    // flightResults 的 key 可能是字符串或数字
    return flightResults[String(flightId)] || flightResults[flightId] || null;
};

const getLineItemValidationStatus = (lineValidationState, validationStale = false) => {
    if (validationStale) return VALIDATION_STATUS.STALE;
    return VALIDATION_STATUS[lineValidationState?.status] || VALIDATION_STATUS.NOT_VALIDATED;
};

/**
 * 渲染校验状态指示器（紧凑版，用于单元格内）
 */
const ValidationStatusIndicator = ({ status, compact = false }) => {
    if (compact) {
        // 超紧凑模式：只显示图标
        return (
            <Tooltip title={status.text}>
                <span style={{ color: status.color, fontSize: 14 }}>{status.icon}</span>
            </Tooltip>
        );
    }

    return (
        <Tag
            color={status.tagColor}
            icon={status.icon}
            style={{
                fontSize: 11,
                padding: "0 4px",
                lineHeight: "16px",
                height: "18px",
                margin: 0,
            }}
        >
            {status.text}
        </Tag>
    );
};

/**
 * 渲染产品级别校验详情 - V6.0 基于 AgentComparisonMatrix
 *
 * 使用统一的 Agent 对比矩阵组件，展示每个 Agent 的独立意见。
 * 紧凑模式适合 Popover 展示。
 */
const ProductValidationDetails = ({ validationResult, productCode, costType, lineValidationState }) => {
    const productsMatrix = extractProductsMatrix(validationResult);

    if (!productsMatrix) {
        return <Empty description="无产品详情" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    const matrixScope = lineValidationState?.reason
        ? {
            scope: lineValidationState.scope,
            productsMatrix: lineValidationState.productsMatrix,
            reason: lineValidationState.reason,
            relatedProductKeys: lineValidationState.relatedProductKeys,
        }
        : resolveProductsMatrixScope(productsMatrix, { productCode, costType });
    const scopedProductsMatrix = matrixScope.scope === "line" && matrixScope.productsMatrix ? matrixScope.productsMatrix : productsMatrix;
    const hasRelatedAliasRows = matrixScope.scope === "line" && (matrixScope.relatedProductKeys || []).length > 0;

    return (
        <div style={{ maxWidth: 500 }}>
            {matrixScope.scope !== "line" && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 8 }}
                    message="航班级验证状态"
                    description="这是航班级状态，当前行无法唯一归属；下方矩阵展示该航班范围内的验证异常，不代表每个产品都属于当前行。"
                />
            )}
            {hasRelatedAliasRows && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 8 }}
                    message="Agent 将金额记到了同费用类型的其他编码"
                    description="下方同时展示标准编码行与 Agent 实际记账编码行：金额已被提取，只是产品编码与标准答案不一致。"
                />
            )}
            {matrixScope.scope === "line" && lineValidationState?.status === "PASSED" && lineValidationState?.hasOtherProductErrors && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 8 }}
                    message="同航班存在其他产品异常"
                    description="当前行产品验证通过，因此外层状态显示通过；同一航班下仍有其他产品未通过，请在完整验证结果中复核整体风险。"
                />
            )}
            <AgentComparisonMatrix
                productsMatrix={scopedProductsMatrix}
                compact={true}
                voteCount={validationResult?.vote_count || validationResult?.votes?.length || 0}
                relatedProductKeys={matrixScope.relatedProductKeys || []}
            />
        </div>
    );
};

/**
 * 航班详情浮窗内容
 */
const FlightDetailPopoverContent = ({ flight, validationResult, matchReasoning, airportsMap, validationStale = false, lineItemProductCode, lineItemCostType, lineValidationState, allocationBreakdown, productMap }) => {
    const validationStatus = getLineItemValidationStatus(lineValidationState, validationStale);
    const hasValidation = !!validationResult;

    return (
        <div style={{ width: 400, maxHeight: 500, overflow: "auto", paddingRight: 8 }}>
            {/* 航班基本信息 */}
            <div
                style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                }}
            >
                <Space size={8} style={{ marginBottom: 8 }}>
                    <Text strong style={{ fontSize: 16 }}>
                        {flight.flight_number || "-"}
                    </Text>
                    <Tag color={flight.status === "COMPLETED" ? "green" : "orange"}>{flight.status || "未知"}</Tag>
                </Space>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
                    <div>
                        <Text type="secondary">飞机注册号:</Text> <Text>{flight.aircraft_reg || "-"}</Text>
                    </div>
                    <div>
                        <Text type="secondary">航线:</Text> <Text>{`${flight.departure_airport || "?"} → ${flight.arrival_airport || "?"}`}</Text>
                    </div>
                    <div>
                        <Text type="secondary">STD:</Text> {flight.std ? <FlightTimeDisplay utcTime={flight.std} airportCode={flight.departure_airport} airportsMap={airportsMap} format="MM-DD HH:mm" showTimezone={true} /> : "-"}
                    </div>
                    <div>
                        <Text type="secondary">STA:</Text> {flight.sta ? <FlightTimeDisplay utcTime={flight.sta} airportCode={flight.arrival_airport} airportsMap={airportsMap} baseUtcTime={flight.std} format="MM-DD HH:mm" showTimezone={true} /> : "-"}
                    </div>
                    <div>
                        <Text type="secondary">ATD:</Text> {(() => { const r = resolveTime(flight, "atd"); return r.value ? <><FlightTimeDisplay utcTime={r.value} airportCode={flight.departure_airport} airportsMap={airportsMap} format="MM-DD HH:mm" showTimezone={true} /><Tag color={r.source === "settlement" ? "blue" : "default"} style={{ fontSize: "9px", marginLeft: 4, padding: "0 3px" }}>{r.source === "settlement" ? "结算" : "MVT"}</Tag></> : "-"; })()}
                    </div>
                    <div>
                        <Text type="secondary">ATA:</Text> {(() => { const r = resolveTime(flight, "ata"); const rAtd = resolveTime(flight, "atd"); return r.value ? <><FlightTimeDisplay utcTime={r.value} airportCode={flight.arrival_airport} airportsMap={airportsMap} baseUtcTime={rAtd.value || flight.std} format="MM-DD HH:mm" showTimezone={true} /><Tag color={r.source === "settlement" ? "blue" : "default"} style={{ fontSize: "9px", marginLeft: 4, padding: "0 3px" }}>{r.source === "settlement" ? "结算" : "MVT"}</Tag></> : "-"; })()}
                    </div>
                </div>

                {/* 航班状态警告 */}
                {flight.status !== "COMPLETED" && (
                    <div
                        style={{
                            marginTop: 8,
                            padding: "6px 10px",
                            background: "#fffbe6",
                            border: "1px solid #ffe58f",
                            borderRadius: 4,
                        }}
                    >
                        <ExclamationCircleOutlined style={{ color: "#faad14", marginRight: 6 }} />
                        <Text style={{ fontSize: 12, color: "#d48806" }}>航班状态非 COMPLETED，不能审核通过</Text>
                    </div>
                )}
            </div>

            {/* 匹配推理过程 */}
            {matchReasoning && (
                <div style={{ marginBottom: 12 }}>
                    <Divider style={{ margin: "8px 0" }}>
                        <Space size={4}>
                            <RocketOutlined style={{ color: "#722ed1" }} />
                            <Text style={{ fontSize: 12, color: "#722ed1" }}>匹配推理</Text>
                        </Space>
                    </Divider>
                    <div
                        style={{
                            background: "#f9f0ff",
                            border: "1px solid #d3adf7",
                            borderRadius: 6,
                            padding: 10,
                            fontSize: 12,
                            lineHeight: 1.6,
                        }}
                    >
                        {matchReasoning}
                    </div>
                </div>
            )}

            {/* 校验结果 */}
            <Divider style={{ margin: "8px 0" }}>
                <Space size={4}>
                    {validationStatus.icon}
                    <Text style={{ fontSize: 12, color: validationStatus.color }}>校验结果</Text>
                </Space>
            </Divider>

            {validationStale ? (
                <Alert
                    type="info"
                    showIcon
                    message="该行已修改，待重新验证航班"
                    description="当前关联航班或费用字段已被人工修改，旧验证结果来自修改前的 payload，不再作为这一行的当前事实结论。请保存后重新触发验证。"
                />
            ) : hasValidation ? (
                <div>
                    {/* 产品级别详情（Agent 对比矩阵已包含匹配/不一致信息） */}
                    <ProductValidationDetails validationResult={validationResult} productCode={lineItemProductCode} costType={lineItemCostType} lineValidationState={lineValidationState} />
                </div>
            ) : (
                <div
                    style={{
                        textAlign: "center",
                        padding: 20,
                        background: "#fafafa",
                        borderRadius: 8,
                    }}
                >
                    <QuestionCircleOutlined style={{ fontSize: 32, color: "#bfbfbf", marginBottom: 8 }} />
                    <div>
                        <Text type="secondary">该航班暂无校验数据</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        校验将在费用分摊完成后自动触发
                    </Text>
                </div>
            )}

            {/* 费用分摊 */}
            <AllocationInfoBlock breakdown={allocationBreakdown} flightId={flight.id} productMap={productMap} />
        </div>
    );
};

/**
 * 单个航班标签（带校验状态）
 */
const FlightTagWithValidation = ({ flight, validationResult, matchReasoning, airportsMap, isCompact = false, validationStale = false, lineItemProductCode, lineItemCostType, allocationBreakdown, productMap }) => {
    const productsMatrix = extractProductsMatrix(validationResult);
    const lineValidationState = resolveLineItemValidationState(validationResult, {
        productsMatrix,
        productCode: lineItemProductCode,
        costType: lineItemCostType,
    });
    const validationStatus = getLineItemValidationStatus(lineValidationState, validationStale);
    const showOtherProductRisk = !validationStale && lineValidationState.scope === "line" && lineValidationState.status === "PASSED" && lineValidationState.hasOtherProductErrors;

    const flightNumber = flight.flight_number || `FM${flight.id}`;
    const rAtd = resolveTime(flight, "atd");
    const flightDate = rAtd.value ? dayjs.utc(rAtd.value).format("MM-DD") : flight.flight_date ? dayjs.utc(flight.flight_date).format("MM-DD") : "";
    const route = `${flight.departure_airport || "?"}-${flight.arrival_airport || "?"}`;

    // 航班状态颜色（COMPLETED 为绿色，其他为橙色）
    const flightStatusColor = flight.status === "COMPLETED" ? "green" : "orange";
    const hasStatusWarning = flight.status !== "COMPLETED";

    const popoverContent = (
        <FlightDetailPopoverContent
            flight={flight}
            validationResult={validationResult}
            matchReasoning={matchReasoning}
            airportsMap={airportsMap}
            validationStale={validationStale}
            lineItemProductCode={lineItemProductCode}
            lineItemCostType={lineItemCostType}
            lineValidationState={lineValidationState}
            allocationBreakdown={allocationBreakdown}
            productMap={productMap}
        />
    );

    return (
        <Popover content={popoverContent} title={null} trigger="hover" placement="right" overlayStyle={{ maxWidth: 420 }} mouseEnterDelay={0.3} mouseLeaveDelay={0.15}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "3px 6px",
                    borderRadius: 4,
                    cursor: "pointer",
                    background: validationStatus.bgColor,
                    border: `1px solid ${validationStatus.borderColor}`,
                    transition: "all 0.2s",
                    minWidth: 0,
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "none";
                }}
            >
                {/* 左侧：航班信息 */}
                <div style={{ flex: 1, minWidth: 0, marginRight: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {hasStatusWarning && <ExclamationCircleOutlined style={{ color: "#faad14", fontSize: 11 }} />}
                        <Text strong style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {flightNumber}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            {flightDate}
                        </Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {route}
                    </Text>
                </div>

                {/* 右侧：当前行优先校验状态指示器 */}
                <Space size={3}>
                    <ValidationStatusIndicator status={validationStatus} compact={isCompact} />
                    {showOtherProductRisk && (
                        <Tooltip title="当前行验证通过，但同航班其他产品存在异常">
                            <InfoCircleOutlined style={{ color: "#faad14", fontSize: 12 }} />
                        </Tooltip>
                    )}
                </Space>
            </div>
        </Popover>
    );
};

/**
 * 航班验证单元格（主组件）
 *
 * @param {Object} props
 * @param {Object} props.record - 行项目数据
 * @param {Object} props.flightResults - 校验结果映射
 * @param {Object} props.airportsMap - 机场数据映射
 * @param {boolean} props.hasValidationData - 是否有校验数据
 * @param {boolean} props.isModified - 该行项目是否被用户修改过
 */
const FlightValidationCell = ({ record, flightResults, airportsMap, hasValidationData = false, isModified = false, validationStale = false, lineItemProductCode, lineItemCostType, invoiceLineItems = [], productMap, onOpenDrawer }) => {
    const additionalInfo = record.additional_info || {};
    const hasFlightInfo = additionalInfo.flight_number || additionalInfo.aircraft_registration;
    const matchedFlights = record.matched_flights || [];
    const matchedIds = record.matched_flight_ids || [];
    const matchReasoning = record.match_reasoning;

    // 分摊明细：在 line_item 级别 memoize，避免每次悬浮 popover 打开时重复计算
    const allocationBreakdown = useMemo(() => {
        if (!record) return null;
        return buildAllocationBreakdown(record, invoiceLineItems);
    }, [record, invoiceLineItems]);

    // 点击单元格区域 → 打开核对助手抽屉
    const handleCellClick = (e) => {
        // 避免与 Popover 等内部交互冲突
        if (e.target.closest(".ant-popover") || e.target.closest(".ant-btn")) return;
        onOpenDrawer?.();
    };

    // 计算该行所有关联航班的校验状态汇总
    const validationSummary = useMemo(() => {
        if (validationStale || !hasValidationData || matchedFlights.length === 0) {
            return { total: 0, passed: 0, errors: 0, pending: matchedFlights.length };
        }

        let passed = 0;
        let errors = 0;

        matchedFlights.forEach((flight) => {
            const result = getFlightValidationResult(flight.id, flightResults);
            if (result) {
                const productsMatrix = extractProductsMatrix(result);
                const lineValidationState = resolveLineItemValidationState(result, {
                    productsMatrix,
                    productCode: lineItemProductCode,
                    costType: lineItemCostType,
                });
                const status = getLineItemValidationStatus(lineValidationState);
                if (status === VALIDATION_STATUS.PASSED) passed++;
                else if (status === VALIDATION_STATUS.HAS_ERRORS) errors++;
            }
        });

        return {
            total: matchedFlights.length,
            passed,
            errors,
            pending: matchedFlights.length - passed - errors,
        };
    }, [matchedFlights, flightResults, hasValidationData, validationStale, lineItemProductCode, lineItemCostType]);

    // 渲染单个航班
    const renderFlightWithValidation = (flight, idx) => {
        const validationResult = validationStale ? null : getFlightValidationResult(flight.id, flightResults);
        return (
            <FlightTagWithValidation
                key={idx}
                flight={flight}
                validationResult={validationResult}
                matchReasoning={matchReasoning}
                airportsMap={airportsMap}
                isCompact={matchedFlights.length > 2}
                validationStale={validationStale}
                lineItemProductCode={lineItemProductCode}
                lineItemCostType={lineItemCostType}
                allocationBreakdown={allocationBreakdown}
                productMap={productMap}
            />
        );
    };

    // 有实际匹配的航班数据
    if (matchedFlights.length > 0) {
        const MAX_DISPLAY = 2; // 两行高度，最多显示2个
        const hasMore = matchedFlights.length > MAX_DISPLAY;
        const displayFlights = hasMore ? matchedFlights.slice(0, MAX_DISPLAY) : matchedFlights;

        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 3, cursor: "pointer" }} onClick={handleCellClick} title="点击打开核对助手">
                {/* 状态汇总条：显示校验状态 + 修改状态 */}
                {(hasValidationData && matchedFlights.length > 1) || isModified ? (
                    <div
                        style={{
                            display: "flex",
                            gap: 4,
                            marginBottom: 2,
                            fontSize: 10,
                            color: "#8c8c8c",
                        }}
                    >
                        {/* 用户修改状态指示 */}
                        {isModified && (
                            <Tooltip title="该行已被手动修改">
                                <span style={{ color: "#1890ff" }}>
                                    <EditOutlined /> 已修改
                                </span>
                            </Tooltip>
                        )}
                        {validationStale && (
                            <Tooltip title="该行已修改，旧验证结果已失效">
                                <span style={{ color: "#1890ff" }}>
                                    <EditOutlined /> 待重新验证航班
                                </span>
                            </Tooltip>
                        )}
                        {/* 校验状态汇总 */}
                        {!validationStale && hasValidationData && matchedFlights.length > 1 && (
                            <>
                                {validationSummary.passed > 0 && (
                                    <span style={{ color: "#52c41a" }}>
                                        <CheckCircleOutlined /> {validationSummary.passed}
                                    </span>
                                )}
                                {validationSummary.errors > 0 && (
                                    <span style={{ color: "#ff4d4f" }}>
                                        <CloseCircleOutlined /> {validationSummary.errors}
                                    </span>
                                )}
                                {validationSummary.pending > 0 && (
                                    <span>
                                        <ClockCircleOutlined /> {validationSummary.pending}
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                ) : null}

                {/* 航班列表 */}
                {displayFlights.map((flight, idx) => renderFlightWithValidation(flight, idx))}

                {/* 查看更多按钮 */}
                {hasMore && (
                    <Popover
                        title={`关联航班 (共${matchedFlights.length}个)`}
                        trigger="click"
                        placement="right"
                        content={
                            <div style={{ maxHeight: 400, overflowY: "auto", width: 360 }}>
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{matchedFlights.map((flight, idx) => renderFlightWithValidation(flight, idx))}</div>
                            </div>
                        }
                    >
                        <Button
                            type="link"
                            size="small"
                            style={{
                                padding: 0,
                                height: "auto",
                                fontSize: 11,
                                color: "#1890ff",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            +{matchedFlights.length - MAX_DISPLAY} 更多
                        </Button>
                    </Popover>
                )}
            </div>
        );
    }

    // 只有 ID 没有详情
    if (matchedIds.length > 0) {
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, cursor: "pointer" }} onClick={handleCellClick} title="点击打开核对助手">
                {matchedIds.slice(0, 2).map((id, idx) => {
                    const validationResult = validationStale ? null : getFlightValidationResult(id, flightResults);
                    const productsMatrix = extractProductsMatrix(validationResult);
                    const lineValidationState = resolveLineItemValidationState(validationResult, {
                        productsMatrix,
                        productCode: lineItemProductCode,
                        costType: lineItemCostType,
                    });
                    const validationStatus = getLineItemValidationStatus(lineValidationState, validationStale);

                    return (
                        <div
                            key={idx}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "2px 6px",
                                background: validationStatus.bgColor,
                                border: `1px solid ${validationStatus.borderColor}`,
                                borderRadius: 4,
                            }}
                        >
                            <Text style={{ fontSize: 11 }}>ID: {id}</Text>
                            <ValidationStatusIndicator status={validationStatus} compact />
                        </div>
                    );
                })}
                {matchedIds.length > 2 && (
                    <Text type="secondary" style={{ fontSize: 10 }}>
                        +{matchedIds.length - 2} 更多
                    </Text>
                )}
            </div>
        );
    }

    // 有航班信息但匹配失败（或用户手动取消了航班关联）
    if (hasFlightInfo) {
        // 用户手动取消关联时，优先显示修改状态（而非 AI 匹配失败）
        if (isModified) {
            return (
                <Tooltip
                    title={
                        <div>
                            <div>AI 原始线索:</div>
                            <div>航班号: {additionalInfo.flight_number || "-"}</div>
                            <div>注册号: {additionalInfo.aircraft_registration || "-"}</div>
                            <div>日期: {additionalInfo.flight_date || "-"}</div>
                        </div>
                    }
                >
                    <Tag
                        color="blue"
                        style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                        }}
                        onClick={handleCellClick}
                    >
                        <EditOutlined />
                        已取消关联
                    </Tag>
                </Tooltip>
            );
        }

        return (
            <Tooltip
                title={
                    <div>
                        <div>航班号: {additionalInfo.flight_number || "-"}</div>
                        <div>注册号: {additionalInfo.aircraft_registration || "-"}</div>
                        <div>日期: {additionalInfo.flight_date || "-"}</div>
                        {matchReasoning && (
                            <>
                                <Divider style={{ margin: "4px 0" }} />
                                <div style={{ fontSize: 11 }}>推理: {matchReasoning}</div>
                            </>
                        )}
                    </div>
                }
            >
                <Tag
                    color="orange"
                    style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                    }}
                    onClick={handleCellClick}
                >
                    <ExclamationCircleOutlined />
                    匹配失败
                </Tag>
            </Tooltip>
        );
    }

    // 用户手动取消了航班关联（与 AI 匹配失败不同）
    if (isModified) {
        return (
            <Tag
                color="blue"
                style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                }}
                onClick={handleCellClick}
            >
                <EditOutlined />
                已取消关联
            </Tag>
        );
    }

    // 无任何航班信息
    return (
        <Tag
            color="red"
            style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
            }}
            onClick={handleCellClick}
        >
            <CloseCircleOutlined />
            未匹配
        </Tag>
    );
};

export default FlightValidationCell;
